import { t as dedupe } from "../../dedupe-Cky1gSUu.js";
import { t as createStore } from "../../store-C9FQCHGe.js";
import { s as HttpHeader } from "../../createFetchClient-CkHdS_IG.js";
import { n as isUnauthorized } from "../../middleware-CWaLMlG1.js";
import { t as createDeveloperPortalClient } from "../../developerPortalClient-Bb2suezr.js";
//#region src/sdks/developerPortal/tokenExpiry.ts
/**
* Reads a JWT's `exp` claim as milliseconds, or `null` when unparseable ("expiry unknown", which the
* caller leaves to the 401 path). A read, not a verification.
*
* Needed because apps-api reports `expiresIn` on login but never on refresh, so it cannot keep a
* proactive-refresh clock honest past the first rotation. The token always carries its own expiry.
*/
const readTokenExpiry = (token) => {
	const segments = token.split(".");
	if (segments.length !== 3) return null;
	const payload = segments[1];
	if (payload === void 0 || payload === "") return null;
	try {
		const base64 = payload.replaceAll("-", "+").replaceAll("_", "/");
		const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, "=");
		const claims = JSON.parse(atob(padded));
		if (typeof claims !== "object" || claims === null || !("exp" in claims)) return null;
		const { exp } = claims;
		if (typeof exp !== "number" || !Number.isFinite(exp)) return null;
		return exp * 1e3;
	} catch {
		return null;
	}
};
//#endregion
//#region src/sdks/developerPortal/credential.ts
const DEFAULT_REFRESH_LEAD_TIME_MS = 3e5;
/** Mirrors `createBearerCredential`, over the id token of the trio. */
const createDeveloperPortalCredential = ({ refresh, getTokens, onSessionExpired, refreshLeadTimeMs = DEFAULT_REFRESH_LEAD_TIME_MS }) => {
	const doRefresh = async () => {
		try {
			return await refresh();
		} catch (error) {
			if (isUnauthorized(error)) onSessionExpired?.();
			throw error;
		}
	};
	const refreshShared = dedupe(doRefresh);
	const getIdToken = async ({ forceRefresh = false } = {}) => {
		const tokens = getTokens();
		if (tokens === null) return null;
		if (forceRefresh) return (await refreshShared()).token;
		const expiresAt = readTokenExpiry(tokens.token);
		if (expiresAt === null) return tokens.token;
		if (Date.now() < expiresAt - refreshLeadTimeMs) return tokens.token;
		return refreshShared().then((fresh) => fresh.token).catch(() => tokens.token);
	};
	return {
		getIdToken,
		getTokens,
		notifyExpired: () => onSessionExpired?.()
	};
};
//#endregion
//#region src/sdks/developerPortal/middleware.ts
/**
* Stamps the id token, and on a 401 force-refreshes and replays once. Mirrors
* `createBearerAuthMiddleware`, minus the `Bearer ` prefix apps-api does not use.
*
* An anonymous session passes through unstamped, so a failed login is never read as session expiry.
*/
const createDeveloperPortalAuthMiddleware = (credential) => {
	return (next) => async (request) => {
		const token = await credential.getIdToken();
		if (token === null) return next(request);
		const retryRequest = request.request.clone();
		request.request.headers.set(HttpHeader.AUTHORIZATION, token);
		try {
			return await next(request);
		} catch (error) {
			if (!isUnauthorized(error)) throw error;
			const refreshed = await credential.getIdToken({ forceRefresh: true }).catch(() => null);
			if (refreshed === null) throw error;
			retryRequest.headers.set(HttpHeader.AUTHORIZATION, refreshed);
			try {
				return await next({
					...request,
					request: retryRequest
				});
			} catch (retryError) {
				if (isUnauthorized(retryError)) credential.notifyExpired();
				throw retryError;
			}
		}
	};
};
//#endregion
//#region src/sdks/developerPortal/developerPortalSdk.ts
/**
* `createDeveloperPortalClient` plus the session that keeps it authenticated.
*
* - Builds its own client: middleware, credential and session depend on each other in a cycle.
*/
const createDeveloperPortalSdk = ({ baseUrl, tokens: initialTokens = null, onTokensChange, onSessionExpired, refreshLeadTimeMs, middlewares = [] }) => {
	const store = createStore(initialTokens);
	store.subscribe((value) => onTokensChange?.(value));
	const getTokens = () => store.get();
	const requireTokens = (action) => {
		const tokens = store.get();
		if (tokens === null) throw new Error(`Developer Portal: ${action} requires a signed-in session.`);
		return tokens;
	};
	const refreshClient = createDeveloperPortalClient({
		baseUrl,
		middlewares
	});
	const refresh = async () => {
		const before = store.get();
		if (before === null) throw new Error("Cannot refresh the Developer Portal session: signed out.");
		const { token, accessToken } = await refreshClient.auth.refreshTokens(before.refreshToken);
		const next = {
			token,
			accessToken,
			refreshToken: before.refreshToken
		};
		const after = store.get();
		if (after === null || after.refreshToken !== before.refreshToken) throw new Error("Developer Portal session changed while refreshing; discarding the result.");
		store.set(next);
		return next;
	};
	const clear = () => store.set(null);
	const credential = createDeveloperPortalCredential({
		refresh,
		getTokens,
		refreshLeadTimeMs,
		onSessionExpired: () => {
			clear();
			onSessionExpired?.();
		}
	});
	const client = createDeveloperPortalClient({
		baseUrl,
		middlewares,
		authMiddleware: createDeveloperPortalAuthMiddleware(credential)
	});
	const { refreshTokens: _storeBypassingRotation, ...auth } = client.auth;
	return {
		api: {
			...client,
			auth: {
				...auth,
				logout: async (signal) => client.auth.logout(requireTokens("sign-out").accessToken, signal),
				setupMfa: async (signal) => {
					await credential.getIdToken();
					const { token, accessToken } = requireTokens("MFA enrolment");
					return client.auth.setupMfa({
						idToken: token,
						accessToken
					}, signal);
				},
				confirmMfa: async (code, signal) => {
					await credential.getIdToken();
					const { token, accessToken } = requireTokens("MFA confirmation");
					return client.auth.confirmMfa({
						idToken: token,
						accessToken,
						code
					}, signal);
				}
			}
		},
		session: {
			set: (result) => {
				store.set({
					token: result.token,
					accessToken: result.accessToken,
					refreshToken: result.refreshToken
				});
			},
			get: getTokens,
			subscribe: store.subscribe,
			clear,
			/** Rotates if the id token is inside the lead time. Best-effort; the 401 path is the guarantee. */
			ensureFresh: async () => {
				await credential.getIdToken();
			},
			/** Rotates now, for a change of claims (e.g. leaving a vendor). Propagates failure. */
			refresh: async () => {
				await credential.getIdToken({ forceRefresh: true });
			},
			isAuthenticated: () => store.get() !== null
		}
	};
};
//#endregion
export { createDeveloperPortalSdk };

//# sourceMappingURL=index.js.map